import React, { ChangeEvent, FC } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { InvoiceLineItem } from 'models/invoice';
import NumberOnlyTextField2 from 'components/number-only-text-field/number-only-text-field-2';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';

type AddLineItemDialogComponentProps = {
    open: boolean;
    lineItem: InvoiceLineItem;
    onDialogClose: () => void;
    onLineAdd: (line: InvoiceLineItem) => void;
};
const AddLineItemDialogComponent: FC<AddLineItemDialogComponentProps> = ({
    open,
    lineItem,
    onDialogClose,
    onLineAdd,
}: AddLineItemDialogComponentProps) => {
    const { t: translate } = useTranslation(['common', 'invoices', 'orders']);

    const validate = (values: InvoiceLineItem) => {
        const errors: Record<string, string> = {};
        if (!values.description) {
            errors.description = translate('invoices:VALIDATION:LINE_ITEM_DESCRIPTION');
        }
        if (!Number.isInteger(parseInt(values.quantity.toString()))) {
            errors.quantity = translate('invoices:VALIDATION:LINE_ITEM_QUANTITY');
        } else if (values.quantity <= 0) {
            errors.quantity = translate('invoices:VALIDATION:LINE_ITEM_QUANTITY_MIN');
        }
        if (!Number.isInteger(parseInt(values.itemPrice.toString()))) {
            errors.itemPrice = translate('invoices:VALIDATION:LINE_ITEM_PRICE');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            description: lineItem.description,
            articleNo: lineItem.articleNo,
            quantity: lineItem.quantity,
            itemPrice: lineItem.itemPrice,
            itemVatPercentage: lineItem.itemVatPercentage,
            itemVat: lineItem.itemVat,
            totalExclVat: lineItem.totalExclVat || 0,
            totalIncVat: lineItem.totalIncVat || 0,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: InvoiceLineItem, { resetForm }) => {
            onLineAdd({ ...values, isManual: true, id: lineItem.id });
            resetForm();
        },
    });

    const handleQuantityChange = (quantity: number) => {
        formik.setFieldValue('quantity', quantity);

        const totalExclVat = (formik.values.itemPrice || 0) * (Number(quantity) || 0);
        formik.setFieldValue('totalExclVat', totalExclVat);

        const itemVat = (totalExclVat * (Number(formik.values.itemVatPercentage) || 0)) / 100;
        formik.setFieldValue('itemVat', itemVat);

        formik.setFieldValue('totalIncVat', totalExclVat + itemVat);
    };

    const handlePriceChange = (itemPrice: string) => {
        if (Number(itemPrice) < 0) {
            formik.setFieldValue('itemVatPercentage', 0);
        }
        formik.setFieldValue('itemPrice', Number(itemPrice));

        const totalExclVat = (formik.values.quantity || 0) * (Number(itemPrice) || 0);
        formik.setFieldValue('totalExclVat', totalExclVat);

        const itemVat = (totalExclVat * (Number(formik.values.itemVatPercentage) || 0)) / 100;
        formik.setFieldValue('itemVat', itemVat);

        formik.setFieldValue('totalIncVat', totalExclVat + itemVat);
    };

    const handleVatChange = (itemVatPercentage: number) => {
        formik.setFieldValue('itemVatPercentage', itemVatPercentage);

        const totalExclVat = (formik.values.quantity || 0) * (formik.values.itemPrice || 0);

        const itemVat = (totalExclVat * (Number(itemVatPercentage) || 0)) / 100;
        formik.setFieldValue('itemVat', itemVat);

        formik.setFieldValue('totalIncVat', totalExclVat + itemVat);
    };

    const handleDialogClose = () => {
        onDialogClose();
        formik.resetForm();
    };

    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        const description = event.target.value;
        if (description.length > 50) {
            event.preventDefault();
            return;
        }
        formik.setFieldValue('description', description);
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={handleDialogClose}>
            <DialogTitle>{translate('invoices:ADD_LINE_ITEMS')}</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Box mt={1}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="description"
                                        label={translate('common:DESCRIPTION')}
                                        required
                                        helperText={formik.touched.description && formik.errors.description}
                                        error={formik.touched.description && Boolean(formik.errors.description)}
                                        {...formik.getFieldProps('description')}
                                        onChange={handleDescriptionChange}
                                        onBlur={formik.getFieldProps('description').onBlur}
                                        name={formik.getFieldProps('description').name}
                                        value={formik.getFieldProps('description').value}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={3}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:QUANTITY')}
                                        fullWidth
                                        hasDecimal={false}
                                        onValueChange={handleQuantityChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('quantity').name}
                                        value={formik.values.quantity}
                                        helperText={formik.touched.quantity && formik.errors.quantity}
                                        error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl margin="dense" fullWidth>
                                    <MonetaryValueInput
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:UNIT_PRICE')}
                                        fullWidth
                                        onValueChange={handlePriceChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('itemPrice').name}
                                        value={formik.getFieldProps('itemPrice').value}
                                        helperText={formik.touched.itemPrice && formik.errors.itemPrice}
                                        error={formik.touched.itemPrice && Boolean(formik.errors.itemPrice)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        disabled
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:TOTAL')}
                                        fullWidth
                                        value={formik.values.totalExclVat}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:VAT_PERCENT')}
                                        fullWidth
                                        hasDecimal={false}
                                        disabled={formik.values.itemPrice < 0}
                                        onValueChange={handleVatChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('itemVatPercentage').name}
                                        value={formik.getFieldProps('itemVatPercentage').value}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        disabled
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:TOTAL_INCL_VAT')}
                                        fullWidth
                                        value={formik.values.totalIncVat}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
                    <Button disabled={!formik.isValid} variant="contained" type="submit">
                        {translate('common:ADD')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddLineItemDialogComponent;
